import React from 'react'; // Import React to use JSX
import { MSL_TABLE_HEADER_SUBTEXT } from '@constants/commonConstants';
import TableHeader from '@molecules/msl/components/forecast/tableHeader';

function formatCurrencyWithoutCents(amount) {
  // Convert to number (ensure it handles cases like "0.000", "1200.00")
  const numericValue = parseFloat(amount); // Default to 0 if null or invalid
  // Handle the special case for 0.000 to show "$0"
  if (numericValue === 0) {
    return '$0';
  }
  // Remove the decimals by flooring the value to the nearest dollar
  const dollarsOnly = Math.floor(numericValue); // Truncate decimals
  // Format the number as currency with commas (US-style grouping)
  const formattedValue = dollarsOnly.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  // Prepend the dollar sign
  return `$${formattedValue}`;
}

const wonVsForecasted = (params, forecastedField, wonField) => {
  const wonValue = params.data[wonField] || 0;
  const forecastedValue = params.data[forecastedField] || 0;

  const formattedWonValue = formatCurrencyWithoutCents(wonValue);
  const formattedForecastedValue = formatCurrencyWithoutCents(forecastedValue);

  // Combine the values into a single string
  const dataValue = `${formattedWonValue} (${formattedForecastedValue})`;

  // Handle wrapping for values with 3 or more characters
  if (dataValue.length >= 3) {
    // Split the value into two parts: all but the last character
    const mainValue = dataValue.slice(0, -4); // All characters except the last one
    const lastValue = dataValue.slice(-4); // The last character

    return (
      <div
        style={{
          whiteSpace: 'pre-wrap',
          wordWrap: 'break-word',
          textAlign: 'right',
          lineHeight: '1.2',
        }}
      >
        <span>{mainValue}</span>
        <span style={{ display: 'block', marginTop: '5px' }}>{lastValue}</span>{' '}
      </div>
    );
  }

  // Return the value as is if it's less than 3 characters
  return <div style={{ textAlign: 'right' }}>{dataValue}</div>;
};
const cellTextAligment = {
  display: 'flex',
  justifyContent: 'flex-end',
  textAlign: 'right',
  whiteSpace: 'pre-wrap',
  wordWrap: 'break-word',
};
export const columnDefsKeys = [
  {
    headerName: 'Account Segment',
    cellRenderer: 'agGroupCellRenderer',
    cellStyle: { fontWeight: 600 },
    tooltipField: 'account_segment',
    flex: 1,
    minWidth: 520,
    suppressMovable: true,
    hide: window.innerWidth < 600,
    filter: true,
    sortable: false,
    valueGetter: params => {
      return params?.data?.forecasted_acc_seg || params?.data?.won_acc_seg;
    },
    valueFormatter: params => {
      if (params.value && params.value !== 'Inbound') {
        return `${params.value} (Headquartered)`;
      }
      return params.value;
    },
  },
  {
    headerName: 'Assurance',
    field: 'won_assurance',
    minWidth: 150,
    hide: window.innerWidth < 600,
    suppressMovable: true,
    enableColumnMenu: true, // Enable the "3 dots" icon for this column
    headerComponentParams: {
      subText: MSL_TABLE_HEADER_SUBTEXT.EXCL_ACR,
      subText2: MSL_TABLE_HEADER_SUBTEXT.WON_FORECASTED,
      enableMenu: false,
      enableFilter: false,
    },
    cellRenderer: params => wonVsForecasted(params, 'assurance', 'won_assurance'),
    cellStyle: cellTextAligment,
    headerComponent: TableHeader,
    sortable: true,
    tooltipValueGetter: params => {
      const assuranceValue = params?.data?.assurance || 0;
      const wonAssuranceValue = params?.data?.won_assurance || 0;
      const formattedAssuranceValue = formatCurrencyWithoutCents(assuranceValue);
      const formattedWonAssuranceValue = formatCurrencyWithoutCents(wonAssuranceValue);
      return `${formattedWonAssuranceValue}(${formattedAssuranceValue})`;
    }, // Custom tooltip combining both fields
  },
  {
    headerName: 'Consulting',
    field: 'won_consulting',
    minWidth: 150,
    hide: window.innerWidth < 600,
    suppressMovable: true,
    cellRenderer: params => wonVsForecasted(params, 'consulting', 'won_consulting'),
    cellStyle: cellTextAligment,
    headerComponentParams: {
      subText: MSL_TABLE_HEADER_SUBTEXT.WON_FORECASTED,
      enableMenu: false,
      enableFilter: false,
    },
    sortable: true,
    tooltipValueGetter: params => {
      const consultingValue = params?.data?.consulting || 0;
      const wonConsultingValue = params?.data?.won_consulting || 0;

      const formattedConsultingValue = formatCurrencyWithoutCents(consultingValue);
      const formattedWonConsultingValue = formatCurrencyWithoutCents(wonConsultingValue);
      return `${formattedWonConsultingValue}(${formattedConsultingValue})`;
    }, // Custom tooltip combining both fields
  },
  {
    headerName: 'SAT',
    field: 'won_sat',
    minWidth: 150,
    hide: window.innerWidth < 600,
    suppressMovable: true,
    headerComponentParams: {
      subText: MSL_TABLE_HEADER_SUBTEXT.EXCL_SAT,
      subText2: MSL_TABLE_HEADER_SUBTEXT.WON_FORECASTED,
      enableMenu: false,
      enableFilter: false,
    },
    cellRenderer: params => wonVsForecasted(params, 'sat', 'won_sat'),
    cellStyle: cellTextAligment,
    sortable: true,
    tooltipValueGetter: params => {
      const satValue = params?.data?.sat || 0;
      const wonSatValue = params?.data?.won_sat || 0;

      const formattedSatValue = formatCurrencyWithoutCents(satValue);
      const formattedWonSatValue = formatCurrencyWithoutCents(wonSatValue);
      return `${formattedWonSatValue}(${formattedSatValue})`;
    }, // Custom tooltip combining both fields
  },

  {
    headerName: 'Tax',
    field: 'won_tax',
    minWidth: 150,
    hide: window.innerWidth < 600,
    suppressMovable: true,
    headerComponentParams: {
      subText: MSL_TABLE_HEADER_SUBTEXT.EXCL_TAX,
      subText2: MSL_TABLE_HEADER_SUBTEXT.WON_FORECASTED,
      enableMenu: false,
      enableFilter: false,
    },
    cellRenderer: params => wonVsForecasted(params, 'tax', 'won_tax'),
    cellStyle: cellTextAligment,
    sortable: true,
    tooltipValueGetter: params => {
      const taxValue = params?.data?.tax || 0;
      const wonTaxValue = params?.data?.won_tax || 0;
      const formattedTaxValue = formatCurrencyWithoutCents(taxValue);
      const formattedWonTaxValue = formatCurrencyWithoutCents(wonTaxValue);
      return `${formattedWonTaxValue}(${formattedTaxValue})`;
    }, // Custom tooltip combining both fields
  },
  {
    headerName: 'ITTS',
    field: 'won_itts',
    minWidth: 150,
    hide: window.innerWidth < 600,
    suppressMovable: true,
    cellRenderer: params => wonVsForecasted(params, 'itts', 'won_itts'),
    cellStyle: cellTextAligment,
    headerComponentParams: {
      subText: MSL_TABLE_HEADER_SUBTEXT.WON_FORECASTED,
      enableMenu: false,
      enableFilter: false,
    },
    sortable: true,
    tooltipValueGetter: params => {
      const ittsValue = params?.data?.itts || 0;
      const wonIttsValue = params?.data?.won_itts || 0;
      const formattedIttsValue = formatCurrencyWithoutCents(ittsValue);
      const formattedWonIttsValue = formatCurrencyWithoutCents(wonIttsValue);
      return `${formattedWonIttsValue}(${formattedIttsValue})`;
    }, // Custom tooltip combining both fields
  },
  {
    headerName: 'EMEIA ACR',
    field: 'won_emiaacr',
    minWidth: 150,
    hide: window.innerWidth < 600,
    suppressMovable: true,
    cellStyle: cellTextAligment,
    cellRenderer: params => wonVsForecasted(params, 'emiaacr', 'won_emiaacr'),
    headerComponentParams: {
      subText: MSL_TABLE_HEADER_SUBTEXT.WON_FORECASTED,
      enableMenu: false,
      enableFilter: false,
    },
    sortable: true,
    tooltipValueGetter: params => {
      const emiaacrValue = params?.data?.emiaacr || 0;
      const wonEmiaacrValue = params?.data?.won_emiaacr || 0;
      const formattedEmiaacrValue = formatCurrencyWithoutCents(emiaacrValue);
      const formattedWonEmiaacrValue = formatCurrencyWithoutCents(wonEmiaacrValue);
      return `${formattedWonEmiaacrValue}(${formattedEmiaacrValue})`;
    }, // Custom tooltip combining both fields
  },
  {
    headerName: 'Total',
    field: 'won_total',
    minWidth: 150,
    hide: window.innerWidth < 600,
    suppressMovable: true,
    cellStyle: cellTextAligment,
    cellRenderer: params => wonVsForecasted(params, 'total', 'won_total'),
    headerComponentParams: {
      subText: MSL_TABLE_HEADER_SUBTEXT.WON_FORECASTED,
      enableMenu: false,
      enableFilter: false,
    },
    sortable: true,
    tooltipValueGetter: params => {
      const totalValue = params?.data?.total || 0;
      const wonTotalValue = params?.data?.won_total || 0;
      const formattedTotalValue = formatCurrencyWithoutCents(totalValue);
      const formattedWonTotalValue = formatCurrencyWithoutCents(wonTotalValue);
      return `${formattedWonTotalValue}(${formattedTotalValue})`;
    }, // Custom tooltip combining both fields
  },
];
//footer table col def
export const mslPastFooterColumnDefs = [
  {
    headerName: '',
    field: 'category',
    sortable: false,
    filter: false,
    suppressMenu: true,
    flex: 1,
    minWidth: 520,
    suppressMovable: true,
    hide: window.innerWidth < 600,
    cellStyle: { textAlign: 'left', fontWeight: 600, fontfamily: 'EYInterstate Light' },
    cellRenderer: 'msl_catagory_CellRenderer',
    tooltipField: 'category',
  },
  {
    headerName: 'Assurance',
    field: 'assurance',
    sortable: false,
    filter: false,
    suppressMenu: true,
    minWidth: 150,
    hide: window.innerWidth < 600,
    suppressMovable: true,
    headerComponent: TableHeader,
    headerComponentParams: {
      subText: MSL_TABLE_HEADER_SUBTEXT.EXCL_ACR,
      enableSorting: false,
      enableMenu: false,
      enableFilter: false,
    },
    cellRenderer: 'past_Msl_CellRenderer',
    cellRendererParams: () => {
      const KeyName = 'Assurance';
      return {
        KeyName,
      };
    },
    cellStyle: { textAlign: 'right' },
    tooltipValueGetter: params => {
      const value = params?.data?.[params.colDef.field] || 0;

      // Check if the category is "Accuracy(Your Total Forecasted vs Actual)"
      if (params?.data?.category === 'Accuracy(Your Total Forecasted vs Actual)') {
        const truncatedValue = Math.floor(value); // Truncate decimals
        return `${truncatedValue}%`; // Return value with %
      }

      // For other categories, format as currency
      return formatCurrencyWithoutCents(value);
    },
  },
  {
    headerName: 'Consulting',
    field: 'consulting',
    sortable: false,
    filter: false,
    suppressMenu: true,
    minWidth: 150,
    hide: window.innerWidth < 600,
    suppressMovable: true,
    cellRenderer: 'past_Msl_CellRenderer',
    cellRendererParams: () => {
      const KeyName = 'Consulting';
      return {
        KeyName,
      };
    },
    cellStyle: { textAlign: 'right' },
    tooltipValueGetter: params => {
      const value = params?.data?.[params.colDef.field] || 0;

      // Check if the category is "Accuracy(Your Total Forecasted vs Actual)"
      if (params?.data?.category === 'Accuracy(Your Total Forecasted vs Actual)') {
        const truncatedValue = Math.floor(value); // Truncate decimals
        return `${truncatedValue}%`; // Return value with %
      }

      // For other categories, format as currency
      return formatCurrencyWithoutCents(value);
    },
  },
  {
    headerName: 'SAT',
    field: 'sat',
    sortable: false,
    filter: false,
    suppressMenu: true,
    minWidth: 150,
    hide: window.innerWidth < 600,
    suppressMovable: true,
    headerComponent: TableHeader,
    headerComponentParams: {
      subText: MSL_TABLE_HEADER_SUBTEXT.EXCL_SAT,
      enableSorting: false,
      enableMenu: false,
      enableFilter: false,
    },
    cellRenderer: 'past_Msl_CellRenderer',
    cellRendererParams: () => {
      const KeyName = 'SAT';
      return {
        KeyName,
      };
    },
    cellStyle: { textAlign: 'right' },
    tooltipValueGetter: params => {
      const value = params?.data?.[params.colDef.field] || 0;

      // Check if the category is "Accuracy(Your Total Forecasted vs Actual)"
      if (params?.data?.category === 'Accuracy(Your Total Forecasted vs Actual)') {
        const truncatedValue = Math.floor(value); // Truncate decimals
        return `${truncatedValue}%`; // Return value with %
      }

      // For other categories, format as currency
      return formatCurrencyWithoutCents(value);
    },
  },
  {
    headerName: 'Tax',
    field: 'tax',
    sortable: false,
    filter: false,
    suppressMenu: true,
    minWidth: 150,
    hide: window.innerWidth < 600,
    suppressMovable: true,
    headerComponent: TableHeader,
    headerComponentParams: {
      subText: MSL_TABLE_HEADER_SUBTEXT.EXCL_TAX,
      enableSorting: false,
      enableMenu: false,
      enableFilter: false,
    },
    cellRenderer: 'past_Msl_CellRenderer',
    cellRendererParams: () => {
      const KeyName = 'Tax';
      return {
        KeyName,
      };
    },
    cellStyle: { textAlign: 'right' },
    tooltipValueGetter: params => {
      const value = params?.data?.[params.colDef.field] || 0;

      // Check if the category is "Accuracy(Your Total Forecasted vs Actual)"
      if (params?.data?.category === 'Accuracy(Your Total Forecasted vs Actual)') {
        const truncatedValue = Math.floor(value); // Truncate decimals
        return `${truncatedValue}%`; // Return value with %
      }

      // For other categories, format as currency
      return formatCurrencyWithoutCents(value);
    },
  },
  {
    headerName: 'ITTS',
    field: 'itts',
    sortable: false,
    filter: false,
    suppressMenu: true,
    minWidth: 150,
    hide: window.innerWidth < 600,
    suppressMovable: true,
    cellRenderer: 'past_Msl_CellRenderer',
    cellRendererParams: () => {
      const KeyName = 'ITTS';
      return {
        KeyName,
      };
    },
    cellStyle: { textAlign: 'right' },
    tooltipValueGetter: params => {
      const value = params?.data?.[params.colDef.field] || 0;

      // Check if the category is "Accuracy(Your Total Forecasted vs Actual)"
      if (params?.data?.category === 'Accuracy(Your Total Forecasted vs Actual)') {
        const truncatedValue = Math.floor(value); // Truncate decimals
        return `${truncatedValue}%`; // Return value with %
      }

      // For other categories, format as currency
      return formatCurrencyWithoutCents(value);
    },
  },
  {
    headerName: 'EMEIA ACR',
    field: 'emeia',
    sortable: false,
    filter: false,
    suppressMenu: true,
    minWidth: 150,
    hide: window.innerWidth < 600,
    suppressMovable: true,
    cellRenderer: 'past_Msl_CellRenderer',
    cellRendererParams: () => {
      const KeyName = 'EMEIA';
      return {
        KeyName,
      };
    },
    cellStyle: { textAlign: 'right' },
    tooltipValueGetter: params => {
      const value = params?.data?.[params.colDef.field] || 0;

      // Check if the category is "Accuracy(Your Total Forecasted vs Actual)"
      if (params?.data?.category === 'Accuracy(Your Total Forecasted vs Actual)') {
        const truncatedValue = Math.floor(value); // Truncate decimals
        return `${truncatedValue}%`; // Return value with %
      }

      // For other categories, format as currency
      return formatCurrencyWithoutCents(value);
    },
  },
  {
    headerName: 'Total',
    field: 'total',
    sortable: false,
    filter: false,
    suppressMenu: true,
    minWidth: 150,
    hide: window.innerWidth < 600,
    suppressMovable: true,
    cellRenderer: 'past_Msl_CellRenderer',
    cellRendererParams: () => {
      const KeyName = 'Total';
      return {
        KeyName,
      };
    },
    cellStyle: { textAlign: 'right', fontWeight: 'bold' },
    tooltipValueGetter: params => {
      const value = params?.data?.[params.colDef.field] || 0;

      // Check if the category is "Accuracy(Your Total Forecasted vs Actual)"
      if (params?.data?.category === 'Accuracy(Your Total Forecasted vs Actual)') {
        const truncatedValue = Math.floor(value); // Truncate decimals
        return `${truncatedValue}%`; // Return value with %
      }

      // For other categories, format as currency
      return formatCurrencyWithoutCents(value);
    },
  },
];
